import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { errorStyle } from '../../../constants/styles';
import Button from '../../Button';
import { CheckboxInput, TextInput } from '../../Inputs';
import { useAuth } from '../AuthContextProvider';
import { AuthValidationError, VIEWS, ViewType } from '../types';
import SocialAuthView from './SocialAuth';
import { toast } from 'react-toastify';

interface EmailLoginViewProps {
  authView: ViewType;
  defaultEmail: string;
  defaultPassword: string;
  setAuthView: any;
  magicLink?: boolean;
}

export interface LoginFormInputs {
  email: string;
  password: string;
  remember: boolean;
}

type InputErrorKey = keyof LoginFormInputs;

const EmailLoginView: React.FC<EmailLoginViewProps> = ({
  defaultEmail,
  defaultPassword,
  setAuthView,
  magicLink,
  authView,
}) => {
  const { actions } = useAuth();

  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginFormInputs>({
    defaultValues: {
      email: defaultEmail,
      password: defaultPassword,
      remember: false,
    },
  });

  useEffect(() => {
    getRememberValues();
  }, [authView]);

  const getRememberValues = async () => {
    try {
      const data: LoginFormInputs | undefined = await actions.getRemembered();
      if (data) {
        setValue('email', data.email);
        setValue('password', data.password);
        setValue('remember', data.remember);
      }
    } catch (error) {}
  };
  const onSubmit = async ({ email, password, remember }: LoginFormInputs) => {
    setLoading(true);

    try {
      await actions.emailLogin(email, password, remember);
    } catch (error) {
      if (error instanceof AuthValidationError) {
        toast(error.message, { type: 'error' });

        const errors = error.errors;
        const keys = Object.keys(errors) as InputErrorKey[];

        keys.forEach(errKey => {
          // @ts-ignore
          setError(errKey, { type: 'api', message: errors[errKey] as string });
        });
      } else {
        console.error(error);
      }
    }

    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center w-full">
      <div className="max-w-sm w-full">
        <form
          id="auth-sign-in"
          onSubmit={handleSubmit(onSubmit)}
          className="w-full"
        >
          <div className="my-6">
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: true,
                pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  id="email"
                  type="email"
                  value={value}
                  placeholder="Email Address"
                  onChange={onChange}
                  invalid={!!errors.email}
                />
              )}
            />

            {errors.email && errors.email.type === 'required' && (
              <span className={errorStyle}>Email address is required</span>
            )}

            {errors.email && errors.email.type === 'pattern' && (
              <span className={errorStyle}>
                Please enter a valid email address
              </span>
            )}

            {errors.email && errors.email.type === 'api' && (
              <span className={errorStyle}>{errors.email.message}</span>
            )}
          </div>

          <div className="my-6">
            <Controller
              name="password"
              control={control}
              defaultValue=""
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  id="password"
                  type="password"
                  placeholder="Password"
                  value={value}
                  onChange={v => onChange(v)}
                  invalid={!!errors.password}
                />
              )}
            />

            {errors.password && errors.password.type === 'required' && (
              <span className={errorStyle}>Password is required</span>
            )}

            {errors.password && errors.password.type === 'api' && (
              <span className={errorStyle}>{errors.password.message}</span>
            )}
          </div>

          <div className="flex items-center justify-between my-6">
            <Controller
              name="remember"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CheckboxInput
                  label="Remember me"
                  id="remember"
                  value={value}
                  onChange={onChange}
                />
              )}
            />

            <a
              href="#auth-forgot-password"
              className="text-gray-450 font-medium hover:text-white text-xs"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                setAuthView(VIEWS.FORGOTTEN_PASSWORD);
              }}
            >
              Forgot your password?
            </a>
          </div>
          <Button
            type="submit"
            text="Sign in"
            buttonStyle="gradiented"
            loading={loading}
            disabled={loading}
          />
          {magicLink && (
            <a
              href="#auth-magic-link"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                setAuthView(VIEWS.MAGIC_LINK);
              }}
              className="text-center text-primary hover:text-primary-dark text-sm mx-auto block my-3 font-medium"
            >
              Send a magic link instead
            </a>
          )}
        </form>
        <SocialAuthView />
      </div>
    </div>
  );
};

export default EmailLoginView;
